/* eslint-disable max-len */
import { ReferenceBrandType } from '@1po/1po-bff-fe-spec/generated/catalog/references/common/model/ReferenceBrandType';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/catalog/search_vehicle/response/VehicleSearchHistoryResponse';

import {
  GET_AVAILABLE_DMS_SERVICES_RESPONSE,
  GET_USER_NOTIFICATION_INIT_RESPONSE,
  GET_USER_NOTIFICATION_UPDATE_RESPONSE,
  GET_USER_PROFILE_RESPONSE,
  GET_USER_SELLERS_PENDING_WARNING,
  GET_USER_SELLERS_RESPONSE,
  GET_VEHICLE_SEARCH_HISTORY_RESPONSE,
} from '@1po/1po-bff-fe-spec/generated/common/ResponseType';
import { AvailableDMSService } from '@1po/1po-bff-fe-spec/generated/dms/model/AvailableDMSService';
import { GetAvailableDMSServicesResponse } from '@1po/1po-bff-fe-spec/generated/dms/response/GetAvailableDMSServicesResponse';

import { CommercialLink, Seller } from '@1po/1po-bff-fe-spec/generated/user/model/CommercialLink';
import { CatalogDisplayType } from '@1po/1po-bff-fe-spec/generated/user/request/UpdateCatalogSettingsRequest';
import { NotificationOnEstimateExpirationType } from '@1po/1po-bff-fe-spec/generated/user/request/UpdateUserNotificationEstimateExpirationRequest';
import { OrderStatusType } from '@1po/1po-bff-fe-spec/generated/user/request/UpdateUserNotificationOrderStatusChangeRequest';
import { NotificationType } from '@1po/1po-bff-fe-spec/generated/user/request/UpdateUserNotificationRequest';
import { PendingSellerWarning } from '@1po/1po-bff-fe-spec/generated/user/response/model/PendingSellerWarning';
import { UserNotificationSettings } from '@1po/1po-bff-fe-spec/generated/user/response/SearchUserProfileResponse';
import { UserNotificationResponse } from '@1po/1po-bff-fe-spec/generated/user/response/UserNotificationResponse';
import { UserNotificationsResponse } from '@1po/1po-bff-fe-spec/generated/user/response/UserNotificationsResponse';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { appTracker } from 'app/AppTracker';
import { SELECTED_PROFILE_PARAM } from 'domains/auth/Auth.types';
import {
  CAR_PARTS_TAB,
  IamCatalogBrandsType,
  IamCatalogTabsType,
  OTHER_BRAND_KEY,
} from 'domains/catalog/Catalog.types';
import { mapTradingProfile } from 'domains/user/User.mapper';
import {
  ADD_FAVORITE_TIRE_BRAND,
  ADD_VEHICLE_TO_SEARCH_HISTORY_REQUEST,
  CANCEL_ALL_NOTIFICATIONS_REQUEST,
  CANCEL_NOTIFICATION_REQUEST,
  CHANGE_DOCUMENT_LANGUAGE,
  CHANGE_WEB_LANGUAGE,
  CLEAR_VEHICLE_SEARCH_HISTORY_REQUEST,
  ClientView,
  DISCONNECT_USER,
  DmsErrorType,
  FETCH_USER_NOTIFICATION_REQUEST,
  GET_AVAILABLE_DMS_SERVICES,
  GET_USER_PROFILE,
  GET_USER_PROFILE_RECONNECT,
  GET_USER_SELLERS,
  GET_VEHICLE_SEARCH_HISTORY_REQUEST,
  IGNORE_ALL_NOTIFICATIONS_REQUEST,
  IGNORE_NOTIFICATION_REQUEST,
  LANGUAGE_STORE_KEY,
  LOAD_USER,
  LOGOUT_USER,
  RELOAD_USER_AFTER_RECONNECT,
  REMOVE_FAVORITE_TIRE_BRAND,
  REMOVE_VEHICLE_FROM_SEARCH_HISTORY_REQUEST,
  SPARE_PARTS_VIEW_STORE_KEY,
  RESET_FAVORITE_TIRE_BRANDS_SELECTION,
  SparePartsViewType,
  SparePartsView,
  StandardView,
  TokenProfile,
  UNSUBSCRIBE_ALL_NOTIFICATIONS_REQUEST,
  UPDATE_DH_CATALOG_REFERENCE_VIEW,
  UPDATE_ORDER_VALIDATION_EMAIL_ADDRESSES_REQUEST,
  UPDATE_ORDER_VALIDATION_EMAIL_NOTIFICATION_REQUEST,
  UPDATE_ORDER_VALIDATION_NOTIFICATION_REQUEST,
  UPDATE_USER_NOTIFICATION_ESTIMATE_EXPIRATION_PERIOD_REQUEST,
  UPDATE_USER_NOTIFICATION_ORDER_STATUS_CHANGE_REQUEST,
  UPDATE_USER_NOTIFICATION_SETTINGS_REQUEST,
  UPDATE_USER_PROFILE_LANGUAGES_REQUEST,
  UPDATE_USER_SELLER,
  User,
  USER_NAMESPACE,
  UserRole,
  UsersState,
} from 'domains/user/User.types';
import { DisplayMode, DisplayModeType } from 'styles';
import { FOUND, getData, hasData, isLoading, LOADING, NO_DATA, SEARCH_STATUS } from 'utils';
import { documentLanguages, Language, webLanguages } from 'utils/i18n/Language';
import { detectStoredLanguage, getWebLanguageFromBrowser } from 'utils/i18n/localeDetector';
import { storeLoad, storeSave } from 'utils/storage';

// For now we store language also in session storage and sync it with BE data
function storeSaveLanguages(state: UsersState) {
  storeSave(LANGUAGE_STORE_KEY, {
    web_lang: state.webLanguage.key,
    doc_main: state.documentLanguage.key,
    doc_fallback: state.documentLanguageFallback.key,
  });
}

const getStoredSparePartsView = (): SparePartsViewType => {
  const storedView: unknown = storeLoad(SPARE_PARTS_VIEW_STORE_KEY);
  return typeof storedView === 'string' && SparePartsView.includes(storedView as SparePartsViewType)
    ? (storedView as SparePartsViewType)
    : ClientView;
};

const initDelegationSession = {
  searchStatus: undefined,
  data: undefined,
};

// Init state
export const initialState: UsersState = {
  tokenUser: {
    user: undefined,
    tokenExpired: false,
  },
  userRoles: [],
  userProfileSearchStatus: undefined,
  webLanguage:
    getWebLanguageFromBrowser() ||
    detectStoredLanguage(webLanguages, LANGUAGE_STORE_KEY, 'web_lang') ||
    Language.DEFAULT,
  documentLanguage: detectStoredLanguage(documentLanguages, LANGUAGE_STORE_KEY, 'doc_main') || Language.DEFAULT,
  documentLanguageFallback:
    detectStoredLanguage(documentLanguages, LANGUAGE_STORE_KEY, 'doc_fallback') || Language.DEFAULT,
  commercialLink: null,
  dmsConfig: undefined,
  favoriteTireBrandList: [],
  sellersToChoose: undefined,
  pendingSellerWarning: undefined,
  showSellerPopup: true,
  vehicleSearchHistory: { searchStatus: undefined, data: undefined },
  sparePartsView: getStoredSparePartsView(),
  referenceView: StandardView,
  iamCatalogBrandsView: OTHER_BRAND_KEY,
  iamCatalogTabsView: CAR_PARTS_TAB,
  dhCatalogTabsView: CAR_PARTS_TAB,
  delegation: {
    session: initDelegationSession,
  },
  isReload: false,
  userNotifications: {
    searchStatus: undefined,
  },
  userNotificationSettings: undefined,
};

// Saga actions
export const setCurrentUserSaga = createAction(LOAD_USER);
export const getUserProfileRequestSaga = createAction(GET_USER_PROFILE);
export const getUserProfileReconnectSaga = createAction(GET_USER_PROFILE_RECONNECT);
export const getUserProfileResponseSaga = createAction(GET_USER_PROFILE_RESPONSE);
export const getPendingSellerWarningResponseSaga = createAction(GET_USER_SELLERS_PENDING_WARNING);
export const updateUserProfileLanguagesRequestSaga = createAction<{
  webLanguage: Language;
  documentLanguage: Language;
  documentLanguageFallback: Language;
}>(UPDATE_USER_PROFILE_LANGUAGES_REQUEST);
export const logoutUser = createAction(LOGOUT_USER);
export const disconnectUser = createAction(DISCONNECT_USER);
export const changeWebLanguageSaga = createAction<Language>(CHANGE_WEB_LANGUAGE);
export const changeDocumentLanguageSaga = createAction<{
  documentLanguage: Language;
  documentLanguageFallback: Language;
}>(CHANGE_DOCUMENT_LANGUAGE);
export const getAvailableDMSServicesRequestSaga = createAction<{
  workshopId: string;
}>(GET_AVAILABLE_DMS_SERVICES);
export const getAvailableDMSServicesResponseSaga = createAction(GET_AVAILABLE_DMS_SERVICES_RESPONSE);

export const getUserSellersRequestSaga = createAction(GET_USER_SELLERS);
export const updateDhCatalogReferenceViewRequestSaga = createAction<CatalogDisplayType>(
  UPDATE_DH_CATALOG_REFERENCE_VIEW,
);

export const addFavoriteTireBrandRequestSaga = createAction<{ tireBrand: ReferenceBrandType }>(ADD_FAVORITE_TIRE_BRAND);
export const removeFavoriteTireBrandRequestSaga = createAction<{ tireBrand: ReferenceBrandType }>(
  REMOVE_FAVORITE_TIRE_BRAND,
);
export const resetFavoriteTireBrandRequestSaga = createAction(RESET_FAVORITE_TIRE_BRANDS_SELECTION);

export const updateUserSellerSaga = createAction<string>(UPDATE_USER_SELLER);
export const getUserSellersResponseSaga = createAction(GET_USER_SELLERS_RESPONSE);
export const fetchVehicleSearchHistorySubscriptionSaga = createAction(GET_VEHICLE_SEARCH_HISTORY_REQUEST);
export const fetchAddVehicleToSearchHistoryRequestSaga = createAction<{
  vehicle: VehicleDetail;
}>(ADD_VEHICLE_TO_SEARCH_HISTORY_REQUEST);
export const fetchRemoveVehicleFromSearchHistoryRequestSaga = createAction<{
  vehicleKey: string;
}>(REMOVE_VEHICLE_FROM_SEARCH_HISTORY_REQUEST);
export const fetchClearVehicleSearchHistoryRequestSaga = createAction(CLEAR_VEHICLE_SEARCH_HISTORY_REQUEST);
export const fetchVehicleSearchHistoryResponseSaga = createAction(GET_VEHICLE_SEARCH_HISTORY_RESPONSE);
export const reloadSubscriptionBeUserSaga = createAction(RELOAD_USER_AFTER_RECONNECT);

export const fetchUserNotificationsRequest = createAction(FETCH_USER_NOTIFICATION_REQUEST);
export const fetchUserNotificationsInitResponse = createAction(GET_USER_NOTIFICATION_INIT_RESPONSE);
export const fetchUserNotificationsUpdateResponse = createAction(GET_USER_NOTIFICATION_UPDATE_RESPONSE);
export const cancelNotificationRequest = createAction<{ notificationId: string }>(CANCEL_NOTIFICATION_REQUEST);
export const cancelAllNotificationsRequest = createAction(CANCEL_ALL_NOTIFICATIONS_REQUEST);
export const ignoreNotificationRequest = createAction<{ notificationId: string }>(IGNORE_NOTIFICATION_REQUEST);
export const ignoreAllNotificationsRequest = createAction(IGNORE_ALL_NOTIFICATIONS_REQUEST);

export const updateUserNotificationSettingsRequestSaga = createAction<{
  notificationType: NotificationType;
  enabled: boolean;
}>(UPDATE_USER_NOTIFICATION_SETTINGS_REQUEST);

export const updateUserNotificationOrderStatusChangeRequestSaga = createAction<{
  orderStatus: OrderStatusType;
  enabled: boolean;
}>(UPDATE_USER_NOTIFICATION_ORDER_STATUS_CHANGE_REQUEST);

export const updateUserNotificationEstimateExpirationPeriodRequestSaga = createAction<{
  periods: NotificationOnEstimateExpirationType[];
}>(UPDATE_USER_NOTIFICATION_ESTIMATE_EXPIRATION_PERIOD_REQUEST);

export const unsubscribeFromAllNotificationsRequest = createAction(UNSUBSCRIBE_ALL_NOTIFICATIONS_REQUEST);

export const updateOrderValidationNotificationRequestSaga = createAction<{
  enabled: boolean;
}>(UPDATE_ORDER_VALIDATION_NOTIFICATION_REQUEST);

export const updateOrderValidationEmailNotificationRequestSaga = createAction<{
  enabled: boolean;
}>(UPDATE_ORDER_VALIDATION_EMAIL_NOTIFICATION_REQUEST);

export const updateOrderValidationEmailAddressesRequestSaga = createAction<{
  emailAddresses: string[];
}>(UPDATE_ORDER_VALIDATION_EMAIL_ADDRESSES_REQUEST);

// Slice
const slice = createSlice({
  name: USER_NAMESPACE,
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setResubscribeableFieldsUndefinedForAppReconnection: (state) => {
      state.userProfileSearchStatus = undefined;
      state.vehicleSearchHistory.searchStatus = undefined;
    },
    setResubscribeableFieldsUndefined: (state) => {
      state.vehicleSearchHistory.searchStatus = undefined;
    },
    setUserIsLoading: (state) => {
      state.tokenUser.user = LOADING;
    },
    setTokenExpired: (state, action: PayloadAction<boolean>) => {
      state.tokenUser.tokenExpired = action.payload;
    },
    setUserRoles: (state, action: PayloadAction<Array<UserRole>>) => {
      const isSame =
        state.userRoles.length === action.payload.length && action.payload.every((r, i) => r === state.userRoles[i]);
      if (!isSame) {
        state.userRoles = action.payload;
      }
    },
    setUser: (state, action: PayloadAction<User | undefined>) => {
      state.tokenUser.user = action.payload;
      if (state.tokenUser.user) {
        appTracker.trackUser(state.tokenUser.user.id);
      }
    },
    setSelectedProfile: (state, action: PayloadAction<TokenProfile>) => {
      const { payload } = action;
      const user = state.tokenUser.user;
      if (!isLoading(user) && user !== undefined) {
        sessionStorage.setItem(SELECTED_PROFILE_PARAM, payload.id);
        state.tokenUser.user = {
          ...user,
          selectedProfile: payload,
        };
      }
    },
    setUserProfileSearchStatus: (state, action: PayloadAction<SEARCH_STATUS>) => {
      state.userProfileSearchStatus = action.payload;
      if (action.payload === LOADING) {
        state.userRoles = LOADING;
      }
    },
    setUserNotificationsInit: (state, action: PayloadAction<UserNotificationsResponse>) => {
      state.userNotifications = {
        data: action.payload,
        searchStatus: FOUND,
      };
    },
    setUserNotificationsUpdate: (state, action: PayloadAction<UserNotificationResponse>) => {
      state.userNotifications.data?.notifications?.unshift(action.payload);
      state.userNotifications.searchStatus = FOUND;
    },
    cancelUserNotification: (state, action: PayloadAction<string>) => {
      if (state.userNotifications.data?.notifications) {
        state.userNotifications.data.notifications = state.userNotifications.data.notifications.filter(
          (notification) => notification.notificationId !== action.payload,
        );
      }
    },
    cancelAllUserNotifications: (state) => {
      const currState = state.userNotifications;
      state.userNotifications = {
        ...currState,
        data: {
          notifications: [],
        },
      };
    },
    setUserSellersToChoose: (state, action: PayloadAction<Seller[] | NO_DATA>) => {
      state.sellersToChoose = action.payload;
    },
    setCommercialLink: (state, action: PayloadAction<CommercialLink>) => {
      appTracker.trackGarage(
        `${action.payload.name} - ${
          action.payload.seller?.buyerRelationToSeller || action.payload.isSeller ? 'R1' : 'R?'
        } - ${action.payload.buyerId ?? '????????'}`,
      );
      state.commercialLink = action.payload;
    },
    setFavoriteTireBrandList: (state, action: PayloadAction<Array<ReferenceBrandType>>) => {
      state.favoriteTireBrandList = action.payload;
    },
    setIsReload: (state, action: PayloadAction<boolean>) => {
      state.isReload = action.payload;
    },
    setAvailableDMSServices: (state, action: PayloadAction<GetAvailableDMSServicesResponse>) => {
      const config = state.dmsConfig;
      state.dmsConfig = {
        ...config,
        availableServices: action.payload.services,
        complementaryDmsData: {
          searchStatus: FOUND,
          data: action.payload.complementaryDmsData,
        },
      };
    },
    setAvailableDMSServicesStatus: (state, action: PayloadAction<{ status: NO_DATA; message?: string }>) => {
      const config = state.dmsConfig;
      state.dmsConfig = {
        ...config,
        availableServices: action.payload.status,
        complementaryDmsData: {
          searchStatus: action.payload.status,
        },
        errorMessage: action.payload.message,
      };
    },
    hideDMSErrorPopup: (state) => {
      const config = state.dmsConfig;
      state.dmsConfig = {
        ...config,
        showErrorPopup: false,
      };
    },
    setDMSErrors: (
      state,
      {
        payload,
      }: PayloadAction<{
        errorCode: string | undefined;
        errorMessage: string | undefined;
        errorType: DmsErrorType | undefined;
      }>,
    ) => {
      const config = state.dmsConfig;
      state.dmsConfig = {
        ...config,
        errorCode: payload.errorCode,
        errorMessage: payload.errorMessage,
        errorType: payload.errorType,
        showErrorPopup: true,
      };
    },
    setPendingSellerWarning: (
      state,
      {
        payload,
      }: PayloadAction<{
        pendingWarning: PendingSellerWarning;
      }>,
    ) => {
      state.pendingSellerWarning = payload.pendingWarning;
    },
    setPendingSellerWarningStatus: (state, action: PayloadAction<SEARCH_STATUS>) => {
      state.pendingSellerWarning = action.payload;
    },
    setDelegationSession: (
      state,
      action: PayloadAction<{
        commercialLink: CommercialLink;
        userRoles: UserRole[];
      }>,
    ) => {
      state.delegation.session = {
        data: {
          userRoles: action.payload.userRoles,
          commercialLink: action.payload.commercialLink,
        },
        searchStatus: FOUND,
      };
    },
    resetDelegationSession: (state) => {
      state.delegation.session = initDelegationSession;
    },
    setWebLanguage: (state, action: PayloadAction<Language>) => {
      state.webLanguage = action.payload;
      storeSaveLanguages(state);
    },
    setDocumentLanguages: (
      state,
      action: PayloadAction<{
        lang: Language;
        fallback: Language;
      }>,
    ) => {
      state.documentLanguage = action.payload.lang;
      state.documentLanguageFallback = action.payload.fallback;
      storeSaveLanguages(state);
    },
    setVehicleSearchHistory: (state, { payload }: PayloadAction<VehicleDetail[]>) => {
      state.vehicleSearchHistory = { data: payload, searchStatus: FOUND };
    },
    setVehicleSearchHistoryStatus: (state, { payload }: PayloadAction<SEARCH_STATUS>) => {
      state.vehicleSearchHistory.searchStatus = payload;
    },
    setSparePartsView: (state, { payload }: PayloadAction<SparePartsViewType>) => {
      state.sparePartsView = payload;
    },
    setCatalogReferenceView: (state, { payload }: PayloadAction<CatalogDisplayType>) => {
      state.referenceView = payload;
    },
    setIamCatalogBrandsView: (state, { payload }: PayloadAction<IamCatalogBrandsType>) => {
      state.iamCatalogBrandsView = payload;
    },
    setIamCatalogTabsView: (state, { payload }: PayloadAction<IamCatalogTabsType>) => {
      state.iamCatalogTabsView = payload;
    },
    setShowSellerPopupFalse: (state) => {
      state.showSellerPopup = false;
    },
    setDhCatalogTabsView: (state, { payload }: PayloadAction<IamCatalogTabsType>) => {
      state.dhCatalogTabsView = payload;
    },
    setUserNotificationSettings: (state, action: PayloadAction<UserNotificationSettings | undefined>) => {
      state.userNotificationSettings = action.payload;
    },
  },
});

// Actions
export const {
  setInitialState,
  setResubscribeableFieldsUndefinedForAppReconnection,
  setResubscribeableFieldsUndefined,
  setTokenExpired,
  setDelegationSession,
  setUserIsLoading,
  setUserRoles,
  setUser,
  setCommercialLink,
  setFavoriteTireBrandList,
  setAvailableDMSServices,
  hideDMSErrorPopup,
  setDMSErrors,
  setUserSellersToChoose,
  setUserProfileSearchStatus,
  setIamCatalogBrandsView,
  setIamCatalogTabsView,
  setWebLanguage,
  setDocumentLanguages,
  setIsReload,
  resetDelegationSession,
  setVehicleSearchHistory,
  setVehicleSearchHistoryStatus,
  cancelUserNotification,
  setSparePartsView,
  setCatalogReferenceView,
  setShowSellerPopupFalse,
  setSelectedProfile,
  setPendingSellerWarning,
  setPendingSellerWarningStatus,
  cancelAllUserNotifications,
  setAvailableDMSServicesStatus,
  setUserNotificationsInit,
  setUserNotificationsUpdate,
  setDhCatalogTabsView,
  setUserNotificationSettings,
} = slice.actions;

// Getters/Selectors
export const getIsLoadingUserToken = createSelector(
  (state: RootState) => state.user.tokenUser.user,
  (user) => isLoading(user),
);

export const getTokenUser = createSelector(
  (state: RootState) => state.user.tokenUser.user,
  (current) => getData(current),
);

export const getUserCommercialLink = createSelector(
  (state: RootState) => state.user.commercialLink,
  (commercialLink) => commercialLink,
);

export const getUserSellersToChoose = createSelector(
  (state: RootState) => state.user.sellersToChoose,
  (sellersToChoose) => sellersToChoose,
);

export const getShowSellerPopup = createSelector(
  (state: RootState) => state.user.showSellerPopup,
  (showSellerPopup) => showSellerPopup,
);

export const getTokenUserRights = createSelector(
  (state: RootState) => state.user.userRoles,
  (roles) => roles,
);

export const getWebLanguage = createSelector(
  (state: RootState) => state.user.webLanguage,
  (language) => language || Language.DEFAULT,
);

export const getUserProfileSearchStatus = createSelector(
  (state: RootState) => state.user.userProfileSearchStatus,
  (status) => status,
);

export const getUserSellerWarning = createSelector(
  (state: RootState) => state.user.pendingSellerWarning,
  (status) => status,
);

export const getTokenExpired = createSelector(
  (state: RootState) => state.user.tokenUser,
  (user) => hasData(user.user) && user.tokenExpired,
);

/** Commercial link of the active Connect-as session (buyer↔seller), when present. */
export const getActiveDelegationCommercialLink = createSelector(
  (state: RootState) => state.delegationSession?.activeSession?.data,
  (session) => (session?.active === true ? session.commercialLink : undefined),
);

/** Prefer delegated buyer commercial link while Connect-as is active. */
export const getEffectiveCommercialLink = createSelector(
  getUserCommercialLink,
  getActiveDelegationCommercialLink,
  (commercialLink, delegatedLink) => delegatedLink ?? commercialLink,
);

export const getDealerType = createSelector(getEffectiveCommercialLink, (commercialLink) => {
  if (commercialLink?.seller?.buyerRelationToSeller) {
    return commercialLink.seller.buyerRelationToSeller;
  }
  return commercialLink?.isSeller === true ? 'R1' : undefined;
});

export const getDelegationSession = createSelector(
  (state: RootState) => state.user.delegation.session.data,
  (session) => session,
);

export const getUserRights = createSelector(getTokenUserRights, (roles) => roles);

export const getTradingProfile = createSelector(
  getTokenUser,
  getUserCommercialLink,
  getUserProfileSearchStatus,
  getActiveDelegationCommercialLink,
  (user, commercialLink, searchStatus, delegatedCommercialLink) => {
    if (searchStatus === FOUND && user) {
      return mapTradingProfile(user, commercialLink, delegatedCommercialLink);
    }
    return undefined;
  },
);

export const getDocumentLanguage = createSelector(
  (state: RootState) => state.user.documentLanguage,
  (docLanguage) => docLanguage || Language.DEFAULT,
);

export const getDocumentLanguageFallback = createSelector(
  (state: RootState) => state.user.documentLanguageFallback,
  (docLanguageFallback) => docLanguageFallback || Language.DEFAULT,
);

export const getUserContext = createSelector(
  getWebLanguage,
  getDocumentLanguage,
  getDocumentLanguageFallback,
  getEffectiveCommercialLink,
  getTokenUser,
  getActiveDelegationCommercialLink,
  (webLanguage, docLanguage, fallbackLanguage, commercialLink, user, delegatedLink) => {
    const countryFromDelegation = delegatedLink?.countryCode;
    return {
      webLanguage: webLanguage.key,
      documentLanguage: docLanguage.getLanguage(),
      documentCountryLanguage: docLanguage.getCountryCode(),
      documentFallbackLanguage: fallbackLanguage.getLanguage(),
      documentFallbackCountryLanguage: fallbackLanguage.getCountryCode(),
      // Buyer country while Connect-as; otherwise token user country
      userCountry: countryFromDelegation ?? user?.country ?? 'FR',
      // R1 country stays on the token principal during Connect-as (cross-country catalogs)
      r1Country: countryFromDelegation
        ? user?.country ?? countryFromDelegation
        : commercialLink?.countryCode ?? user?.country ?? 'FR',
    };
  },
);

export const getVehicleSearchHistory = createSelector(
  (state: RootState) => state.user.vehicleSearchHistory,
  (vehicleSearchHistory) => vehicleSearchHistory,
);

export const getSparePartsView = createSelector(
  (state: RootState) => state.user.sparePartsView,
  (sparePartsView) => sparePartsView,
);

export const getCatalogReferenceView = createSelector(
  (state: RootState) => state.user.referenceView,
  (referenceView) => referenceView,
);

export const getFavoriteBrandPreferences = createSelector(
  (state: RootState) => state.user.favoriteTireBrandList,
  (favoriteTireBrandList) => favoriteTireBrandList,
);

export const getIamCatalogBrandsView = createSelector(
  (state: RootState) => state.user.iamCatalogBrandsView,
  (iamCatalogBrandsView) => iamCatalogBrandsView,
);

export const getIamCatalogTabsView = createSelector(
  (state: RootState) => state.user.iamCatalogTabsView,
  (iamCatalogTabsView) => iamCatalogTabsView,
);

export const getDhCatalogTabsView = createSelector(
  (state: RootState) => state.user.dhCatalogTabsView,
  (dhCatalogTabsView) => dhCatalogTabsView,
);

export const getCurrency = createSelector(
  (state: RootState) => state.user.commercialLink?.currencyCode,
  (currency) => currency ?? 'EUR',
);

export const getUserCountry = createSelector(getTokenUser, (tokenUser) => tokenUser?.country);

export const getUserId = createSelector(getTokenUser, (tokenUser) => tokenUser?.id);

export const getSelectedProfile = createSelector(getTokenUser, (user) => user?.selectedProfile);

// Can be used to load user customizable theme
export const getUserDisplayMode = createSelector(getEffectiveCommercialLink, (commercialLink) => {
  const dealerType = commercialLink?.seller?.buyerRelationToSeller;
  const themeDisplayType = dealerType === DisplayMode.R3_MOTRIO ? DisplayMode.R3_MOTRIO : DisplayMode.DARK;
  return themeDisplayType as DisplayModeType;
});

export const isTokenValid = createSelector(
  getTokenUser,
  (user) => user?.id !== undefined && user?.rights !== undefined && user.country !== undefined,
);

export const getIsReload = createSelector(
  (state: RootState) => state.user.isReload,
  (isReload) => isReload,
);

export const getAvailableDMSServices = createSelector(
  (state: RootState) => state.user.dmsConfig,
  (dmsConfig) => dmsConfig?.availableServices,
);

export const getUserTresorCode = createSelector(
  getEffectiveCommercialLink,
  (commercialLink) => commercialLink?.tresorCode,
);

export const getUserSellerBirCode = createSelector(
  getEffectiveCommercialLink,
  (commercialLink) => commercialLink?.seller?.birCode,
);

export const getAvailableDMSService = createSelector(
  (state: RootState) => state.user.dmsConfig,
  (_state: RootState, serviceToFind: AvailableDMSService) => serviceToFind,
  (dmsConfig, serviceToFind) => {
    if (dmsConfig?.availableServices === LOADING) return LOADING;
    return dmsConfig?.availableServices && hasData(dmsConfig.availableServices)
      ? dmsConfig.availableServices.includes(serviceToFind)
      : false;
  },
);

export const getDMSConfig = createSelector(
  (state: RootState) => state.user.dmsConfig,
  (dmsConfig) => dmsConfig,
);

export const getUserNotifications = createSelector(
  (state: RootState) => state.user.userNotifications,
  (userNotifications) => userNotifications,
);

export const getUserNotificationSettings = createSelector(
  (state: RootState) => state.user.userNotificationSettings,
  (roles) => roles,
);

// Export reducer
export default slice.reducer;
